/**
 * Google Identity Services: loads the GSI script on demand and renders the
 * "Sign in with Google" button, handing the ID token to the caller.
 */
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { mountScaledButton } from './google-button-frame';
import { GoogleProfilePhoto } from './google-profile-photo';
import type { Theme } from './theme.service';

interface GoogleAccountsId {
  initialize(config: {
    client_id: string;
    callback: (response: { credential?: string }) => void;
    ux_mode: 'popup';
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

@Injectable({
  providedIn: 'root',
})
export class GoogleIdentityService {
  private scriptLoad?: Promise<GoogleAccountsId>;
  private onCredential?: (credential: string) => void;

  readonly isEnabled = environment.auth.googleClientId !== '';

  /** The photo of the user who last signed in with Google, for the navbar. */
  readonly profilePhoto = new GoogleProfilePhoto(localStorage);

  /**
   * Renders the button into `parent` in one theme. GSI draws it in an iframe,
   * so its look comes from Google's themes rather than our CSS, and its size
   * from scaling it to `parent`'s height.
   */
  async renderButton(
    parent: HTMLElement,
    theme: Theme,
    onCredential: (credential: string) => void,
  ): Promise<void> {
    const accountsId = await this.load();
    this.onCredential = onCredential;
    mountScaledButton(parent, (frame, width) =>
      accountsId.renderButton(frame, {
        type: 'standard',
        theme: theme === 'dark' ? 'filled_black' : 'outline',
        size: 'large',
        shape: 'rectangular',
        text: 'signin_with',
        logo_alignment: 'center',
        width,
      }),
    );
  }

  private load(): Promise<GoogleAccountsId> {
    this.scriptLoad ??= new Promise<GoogleAccountsId>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        const accountsId = window.google?.accounts.id;
        if (!accountsId) {
          reject(new Error('GSI unavailable'));
          return;
        }
        // Initialized once; every rendered button reports to the latest handler.
        accountsId.initialize({
          client_id: environment.auth.googleClientId,
          callback: (response) => {
            if (response.credential) this.onCredential?.(response.credential);
          },
          ux_mode: 'popup',
        });
        resolve(accountsId);
      };
      script.onerror = () => {
        // Let a later visit retry instead of caching the failure.
        this.scriptLoad = undefined;
        reject(new Error('GSI failed to load'));
      };
      document.head.appendChild(script);
    });
    return this.scriptLoad;
  }
}
